import React, { useEffect } from 'react';
import { ModuleDetail } from '../data/moduleDetails';

interface AvoModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleDetail | null;
}

const AvoModuleModal: React.FC<AvoModuleModalProps> = ({ isOpen, onClose, module }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !module) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative bg-surface border border-white/10 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-modalSlideIn">
        {/* Header */}
        <div className="sticky top-0 bg-surface/95 backdrop-blur-sm border-b border-white/10 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-xl text-white text-2xl">
                {module.title.includes('Procure') && '🛒'}
                {module.title.includes('AI') && '🤖'}
                {module.title.includes('eBidding') && '🏆'}
                {module.title.includes('Vendor Portal') && '👥'}
                {module.title.includes('Contract') && '📄'}
                {module.title.includes('Analytics') && '📊'}
                {module.title.includes('Workflow') && '⚙️'}
                {module.title.includes('Invoicing') && '💰'}
                {module.title.includes('Risk Monitor') && '🛡️'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-ink-900">{module.title}</h2>
                <p className="text-ink-500 text-sm">{module.shortDescription}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-brand-start hover:bg-white/5 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-start"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Detailed Description */}
          <div>
            <h3 className="text-lg font-semibold text-ink-900 mb-3">Overview</h3>
            <p className="text-ink-600 leading-relaxed">{module.detailedDescription}</p>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold text-ink-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-start" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Key Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {module.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-start rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-ink-600 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div>
            <h3 className="text-lg font-semibold text-ink-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-start" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Benefits
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {module.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-ink-600 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Use Cases */}
          <div>
            <h3 className="text-lg font-semibold text-ink-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-start" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Ideal For
            </h3>
            <div className="flex flex-wrap gap-2">
              {module.useCases.map((useCase, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-ink-600 text-sm"
                >
                  {useCase}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface/95 backdrop-blur-sm border-t border-white/10 p-6 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition-all duration-200 font-medium"
            >
              Close
            </button>
            <button
              className="px-6 py-2 bg-gradient-to-r from-brand-start to-brand-end text-white rounded-lg hover:from-brand-end hover:to-brand-start transition-all duration-300 font-medium"
              onClick={() => {
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
                onClose();
              }}
            >
              Request Demo
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s cubic-bezier(0.23, 1, 0.32, 1);
        }
      `}</style>
    </div>
  );
};

export default AvoModuleModal; 